import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Contracts } from "../lib/api";
import { Printer, Download, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";

function renderInlineBold(text) {
  // Split text on <b>...</b> markers preserving order.
  const parts = [];
  let remaining = text || "";
  let key = 0;
  while (remaining.includes("<b>")) {
    const [before, rest] = remaining.split(/<b>(.*)/s);
    parts.push(before);
    const [bold, after] = (rest || "").split(/<\/b>(.*)/s);
    parts.push(<strong key={`b-${key++}`}>{bold}</strong>);
    remaining = after || "";
  }
  if (remaining) parts.push(remaining);
  return parts;
}

export default function ContractView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    Contracts.preview(id)
      .then(setData)
      .catch(() => toast.error("Nie znaleziono umowy"))
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-trigger print when the page is opened with ?autoprint=1 (used by the
  // "Drukuj" button — it opens the current URL in a new top-level tab where
  // window.print() actually works, bypassing the Preview iframe sandbox that
  // silently swallows print() calls inside sandboxed frames).
  useEffect(() => {
    if (loading || !data) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("autoprint") === "1") {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, data]);

  const handlePrint = () => {
    // window.print() is blocked in sandboxed iframes (Emergent Preview panel).
    // Detect iframe context and open the same route in a top-level tab where
    // print works natively. Fall back to direct print() when we're already on top.
    const isIframe = window.self !== window.top;
    if (isIframe) {
      const url = new URL(window.location.href);
      url.searchParams.set("autoprint", "1");
      const w = window.open(url.toString(), "_blank");
      if (!w) {
        // Popup blocked — escape sandbox via top-level nav
        const a = document.createElement("a");
        a.href = url.toString();
        a.target = "_top";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      return;
    }
    window.print();
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      await Contracts.download(id, format);
      toast.success(`${format.toUpperCase()} otwarty w nowej karcie`);
    } catch {
      toast.error(`Błąd pobierania ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-zinc-500" data-testid="preview-loading">
        Ładowanie podglądu...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-20 text-center" data-testid="preview-not-found">
        <FileText className="w-12 h-12 mx-auto opacity-30 mb-4" />
        <p className="text-zinc-600">Nie znaleziono umowy.</p>
        <Link
          to="/contracts"
          className="inline-block mt-6 px-6 py-3 border border-zinc-900 font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white"
        >
          Wróć do historii
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-view-page">
      {/* Action bar (hidden in print) */}
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="btn-back-view"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
        >
          <ArrowLeft className="w-4 h-4" /> Wstecz
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handlePrint}
            data-testid="btn-print"
            className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-5 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-[#C5232A]"
          >
            <Printer className="w-4 h-4" /> Drukuj
          </button>
          <button
            onClick={() => handleDownload("pdf")}
            disabled={downloading === "pdf"}
            data-testid="btn-view-download-pdf"
            className="inline-flex items-center gap-2 bg-white border border-zinc-900 text-zinc-900 px-5 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-zinc-100 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {downloading === "pdf" ? "Pobieram..." : "PDF"}
          </button>
          <button
            onClick={() => handleDownload("docx")}
            disabled={downloading === "docx"}
            data-testid="btn-view-download-docx"
            className="inline-flex items-center gap-2 bg-white border border-zinc-900 text-zinc-900 px-5 py-2.5 font-bold uppercase tracking-wider text-sm hover:bg-zinc-100 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {downloading === "docx" ? "Pobieram..." : "DOCX"}
          </button>
        </div>
      </div>

      {/* Header (hidden in print) */}
      <div className="no-print flex items-end justify-between border-b border-zinc-300 pb-4">
        <div>
          <div className="font-label mb-1">// Podgląd</div>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight">
            {data.title}
          </h1>
          <div className="font-mono text-sm text-zinc-500 mt-1">
            Nr {data.contract_number}
          </div>
        </div>
      </div>

      {/* Printable document */}
      <article
        className="print-document mx-auto bg-white border border-zinc-300 shadow-sm"
        data-testid="printable-document"
        style={{
          width: "21cm",
          minHeight: "29.7cm",
          padding: "2cm",
          color: "#18181B",
        }}
      >
        {data.sections.map((s, i) => {
          if (s.kind === "title") {
            return (
              <h1
                key={i}
                className="text-center font-heading font-black uppercase tracking-tight"
                style={{ fontSize: "22pt", marginBottom: "4pt" }}
              >
                {s.text}
              </h1>
            );
          }
          if (s.kind === "subtitle") {
            return (
              <div
                key={i}
                className="text-center font-mono"
                style={{ fontSize: "11pt", color: "#52525B", marginBottom: "22pt" }}
              >
                {s.text}
              </div>
            );
          }
          if (s.kind === "right") {
            return (
              <div
                key={i}
                className="text-right font-mono"
                style={{ fontSize: "10pt", color: "#52525B", marginBottom: "18pt" }}
              >
                {s.text}
              </div>
            );
          }
          if (s.kind === "h2") {
            return (
              <h2
                key={i}
                className="font-bold"
                style={{ fontSize: "12pt", marginTop: "16pt", marginBottom: "6pt" }}
              >
                {s.text}
              </h2>
            );
          }
          if (s.kind === "para") {
            return (
              <p
                key={i}
                className="text-justify"
                style={{
                  fontSize: "11pt",
                  lineHeight: 1.55,
                  marginBottom: "8pt",
                }}
              >
                {renderInlineBold(s.text)}
              </p>
            );
          }
          if (s.kind === "table") {
            return (
              <table
                key={i}
                className="w-full border-collapse"
                style={{ marginBottom: "12pt", fontSize: "10pt" }}
              >
                <tbody>
                  {s.rows.map((row, ri) => (
                    <tr key={ri}>
                      <td
                        className="font-bold"
                        style={{
                          border: "0.5pt solid #D4D4D8",
                          padding: "6pt 8pt",
                          background: "#F4F4F5",
                          width: "35%",
                        }}
                      >
                        {row[0]}
                      </td>
                      <td
                        style={{
                          border: "0.5pt solid #D4D4D8",
                          padding: "6pt 8pt",
                        }}
                      >
                        {row[1]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          }
          if (s.kind === "signatures") {
            return (
              <div
                key={i}
                className="grid grid-cols-2 gap-8 text-center"
                style={{ marginTop: "40pt", fontSize: "10pt", color: "#52525B" }}
              >
                {s.labels.map((lab, li) => (
                  <div key={li}>
                    <div style={{ marginBottom: "6pt" }}>
                      ...........................................
                    </div>
                    <div>{lab}</div>
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })}
      </article>
    </div>
  );
}
